import React from 'react';
import { Link, useLocation } from 'react-router-dom';

const PrivacyPolicyPage: React.FC = () => {
  const location = useLocation();
  const isPublic = !location.pathname.startsWith('/dashboard');
  const backTo = isPublic ? '/' : '/dashboard';
  const backLabel = isPublic ? '← Back to Home' : '← Back';
  return (
    <div className="static-page">
      <Link to={backTo} className="static-page-back">{backLabel}</Link>
      <h1 className="static-page-title">Privacy Policy</h1>
      <p className="static-page-updated">Last updated: February 2026</p>

      <section className="static-page-section">
        <h2>1. Introduction and Data Controller</h2>
        <p>
          arihex.com (“we,” “us,” or “our”) operates the Arihex platform and related services (the “Service”). This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our Service. We are the data controller for the personal data we process in connection with the Service. By using the Service, you consent to the practices described in this policy. If you do not agree, please do not use the Service.
        </p>
      </section>

      <section className="static-page-section">
        <h2>2. Information We Collect</h2>
        <p>
          We collect information that you provide directly to us, that we obtain automatically when you use the Service, and that we receive from third parties where applicable.
        </p>
        <p>
          <strong>Account and profile information:</strong> When you register or update your account, we collect name, email address, company or organization name, and password (stored in hashed form). We may also collect job title, phone number, and billing details if you subscribe to a paid plan.
        </p>
        <p>
          <strong>Content and usage data:</strong> We collect the content you create and manage in the Service, including chatbot names and configurations, system prompts, connected data source metadata (e.g., connection strings are stored securely; we may retain schema or table names for functionality), uploaded file names and types, chat messages and responses generated through the Service, and session and usage data (e.g., features used, actions taken, time spent).
        </p>
        <p>
          <strong>Technical and device information:</strong> We automatically collect IP address, browser type and version, device type, operating system, referring URLs, pages viewed, and similar technical data. We may use cookies, web beacons, and similar technologies as described in Section 10.
        </p>
        <p>
          <strong>Support and communications:</strong> When you contact support or respond to surveys, we collect the content of your communications and any contact details you provide.
        </p>
      </section>

      <section className="static-page-section">
        <h2>3. How We Use Your Information</h2>
        <p>
          We use the information we collect to:
        </p>
        <ul className="static-page-list">
          <li>Provide, operate, maintain, and improve the Service;</li>
          <li>Authenticate users and manage accounts;</li>
          <li>Process transactions and send related information (e.g., confirmations, invoices);</li>
          <li>Send service-related notices, security alerts, and support messages;</li>
          <li>Respond to your requests and provide customer support;</li>
          <li>Monitor and analyze usage, trends, and performance to improve the Service and develop new features;</li>
          <li>Detect, prevent, and address fraud, abuse, and security incidents;</li>
          <li>Comply with legal obligations and enforce our terms and policies;</li>
          <li>Send marketing communications where you have opted in or where we have a legitimate interest, in accordance with applicable law.</li>
        </ul>
      </section>

      <section className="static-page-section">
        <h2>4. Legal Bases for Processing (EEA/UK)</h2>
        <p>
          Where required by law (e.g., in the European Economic Area or the United Kingdom), we process personal data based on: (a) your consent; (b) performance of a contract with you; (c) our legitimate interests (e.g., providing and improving the Service, security, analytics), where not overridden by your rights; (d) compliance with legal obligations; or (e) protection of vital or public interests. You may withdraw consent where processing is consent-based, without affecting the lawfulness of processing before withdrawal.
        </p>
      </section>

      <section className="static-page-section">
        <h2>5. Data Sharing and Disclosure</h2>
        <p>
          We do not sell your personal information. We may share your information in the following circumstances:
        </p>
        <ul className="static-page-list">
          <li><strong>Service providers:</strong> We share data with vendors who perform services on our behalf (e.g., hosting, analytics, payment processing, email delivery, customer support), under contractual obligations to protect your data and use it only for the purposes we specify.</li>
          <li><strong>Legal and safety:</strong> We may disclose information if required by law, court order, or government request, or when we believe in good faith that disclosure is necessary to protect our rights, your safety, or the safety of others, or to investigate fraud or security issues.</li>
          <li><strong>Business transfers:</strong> In the event of a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction, subject to the same privacy commitments.</li>
          <li><strong>With your consent:</strong> We may share information for other purposes with your consent.</li>
        </ul>
      </section>

      <section className="static-page-section">
        <h2>6. International Data Transfers</h2>
        <p>
          Your information may be transferred to and processed in countries other than your country of residence, including countries that may have different data protection laws. Where we transfer personal data from the EEA or UK to countries not deemed to provide adequate protection, we implement appropriate safeguards (e.g., standard contractual clauses approved by relevant authorities) to ensure your data remains protected in accordance with this policy and applicable law.
        </p>
      </section>

      <section className="static-page-section">
        <h2>7. Data Retention</h2>
        <p>
          We retain your information for as long as your account is active or as needed to provide the Service and fulfill the purposes described in this policy. We may retain certain information after account closure to comply with legal obligations, resolve disputes, enforce agreements, and for legitimate business purposes (e.g., backup, analytics in anonymized form). When data is no longer needed, we securely delete or anonymize it in accordance with our retention schedule and applicable law.
        </p>
      </section>

      <section className="static-page-section">
        <h2>8. Security</h2>
        <p>
          We implement appropriate technical and organizational measures to protect your personal data against unauthorized access, alteration, disclosure, or destruction. These measures include encryption in transit and at rest where applicable, access controls, regular security assessments, and staff training. No method of transmission over the Internet or electronic storage is completely secure; we cannot guarantee absolute security but we are committed to protecting your data in line with industry standards and applicable regulations.
        </p>
      </section>

      <section className="static-page-section">
        <h2>9. Your Rights</h2>
        <p>
          Depending on your location, you may have the following rights regarding your personal data:
        </p>
        <ul className="static-page-list">
          <li><strong>Access:</strong> Request a copy of the personal data we hold about you.</li>
          <li><strong>Rectification:</strong> Request correction of inaccurate or incomplete data.</li>
          <li><strong>Erasure:</strong> Request deletion of your personal data, subject to legal and operational requirements.</li>
          <li><strong>Restriction:</strong> Request that we limit processing in certain circumstances.</li>
          <li><strong>Portability:</strong> Request a copy of your data in a structured, machine-readable format where applicable.</li>
          <li><strong>Objection:</strong> Object to processing based on legitimate interests or for direct marketing.</li>
          <li><strong>Withdraw consent:</strong> Where processing is based on consent, withdraw it at any time.</li>
          <li><strong>Complaint:</strong> Lodge a complaint with a supervisory authority in your country.</li>
        </ul>
        <p>
          To exercise these rights, please contact us via the Contact Support page or at support@arihex.com. We will respond within the timeframes required by applicable law. You may also manage certain preferences (e.g., marketing communications) through your account settings or by following unsubscribe links in our emails.
        </p>
      </section>

      <section className="static-page-section">
        <h2>10. Cookies and Similar Technologies</h2>
        <p>
          We use cookies and similar technologies (e.g., local storage, pixels) to provide the Service, remember your preferences, authenticate you, analyze usage, and improve performance. Essential cookies are necessary for the Service to function. We may also use analytics and functional cookies subject to your choices where required by law. You can manage cookies through your browser settings; disabling certain cookies may affect the functionality of the Service. For more information, see our cookie notice or contact us.
        </p>
      </section>

      <section className="static-page-section">
        <h2>11. Children’s Privacy</h2>
        <p>
          The Service is not directed to individuals under the age of 16 (or the applicable age of consent in your jurisdiction). We do not knowingly collect personal data from children. If you become aware that a child has provided us with personal data, please contact us and we will take steps to delete such information.
        </p>
      </section>

      <section className="static-page-section">
        <h2>12. Changes to This Policy</h2>
        <p>
          We may update this Privacy Policy from time to time to reflect changes in our practices, the Service, or legal requirements. We will post the updated policy on this page and update the “Last updated” date. For material changes, we will provide additional notice (e.g., by email or in-product notification) where required or appropriate. We encourage you to review this policy periodically. Your continued use of the Service after the effective date of changes constitutes acceptance of the updated policy.
        </p>
      </section>

      <section className="static-page-section">
        <h2>13. Contact Us</h2>
        <p>
          For privacy-related questions, to exercise your rights, or to contact our data protection officer (where applicable), please use the Contact Support page or email us at support@arihex.com. We will respond to your request in accordance with applicable law.
        </p>
      </section>
    </div>
  );
};

export default PrivacyPolicyPage;
